/* 
Component name: Fork of Table Edit Cells
Product: ATX
Author: Vladyslav Ptychkin
*/


import React, { useRef, useState, useEffect, useCallback } from "react";

import { Icon } from "components/Icon";
import styled, { css } from "styled-components";
import { DEFAULT_PER_PAGE_MEDIUM } from "const";
import BaseTable from "components/base/BaseTable";
import Pagination from "components/base/Pagination";
import ModalSubmitRowApprove from "./ModalSubmitRowApprove";
import ModalRemoveRow from "./ModalRemoveRow";
import FormCreateRowNew from "./FormCreateRowNew";
import { Spacer } from "components/base/Spacer";
import { compareObjects, isNumber, resolvePerPage } from "utils/helpers";
import { focusPickerSearch } from "utils/inputs";

import { flushSync } from "react-dom";
import { Button, Table } from "rsuite";
import Form from "components/Form/CompatForm";

const { Cell, Column, HeaderCell } = Table;


export default (
    {
        rowIdKey = "id",
        defaultRowIdKey = null,
        data = [],
        loading,
        columns,
        editable = true,
        removeExtended = false,
        showFormCreate = true,
        modalRemoveTitle = "Remove item",

        canRemove = true,
        canCreate = true,
        canModify = true,

        customButton,

        onRowSubmit,
        onRowEdit,
        onRowCreate,
        onRowRemove,
        onDataUpdate,
        checkParamsBeforeModify,

        formModel,

        mobile = false,

        pagination = false,
        activePage = 1,
        perPage = DEFAULT_PER_PAGE_MEDIUM,
        defaultPerPage = DEFAULT_PER_PAGE_MEDIUM,
        perPageLSKey = null,
        count = 0,
        getItems,
        onChangePage,
        onChangePerPage,

        extraData = {},
        changeExtraData = null,
        createFormDefaultValue = {},
        clearOnCancel = false,
        extraDisabledModify = false,
        extraDisabledCreate = false,

        createExtraData = {},

        addButtonWidth,

        extraButtons = null,

        resetEditableRowsToken = null,

        setEditKeys = null,
        extraOnChange = null,
        extraOnSubmit = null,
        onChangeServiceName,

        defaultDisableKey = "",

        createPermission = true,
        modifyPermission = true,
        removePermission = true,
        formStyle = {},

        ...props
    }
) => {
    const refs = useRef(new Map());
    const didMountRef = React.useRef(false);

    const [editableRows, setEditableRows] = useState(new Map());
    const [loadableRows, setLoadableRows] = useState([]);

    const [defaultKey, setDefaultKey] = useState(rowIdKey);

    // modal remove
    const [modalRemoveData, setModalRemoveData] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeDataLoading, setRemoveDataLoading] = useState(null);

    // modal approve
    const [modalApproveData, setModalApproveData] = useState(false);
    const [showModalApprove, setShowModalApprove] = useState(false);
    const [modalApproveLoading, setModalApproveLoading] = useState(null);

    const [formStateValue, setFormStateValue] = useState({});
    const [localPerPage, setLocalPerPage] = useState(() => resolvePerPage(
        perPageLSKey,
        perPage,
        defaultPerPage
    ));

    const resolvedPerPage = perPageLSKey
        ? localPerPage
        : (perPage ?? defaultPerPage);

    useEffect(() => {
        const nextPerPage = resolvePerPage(
            perPageLSKey,
            perPage,
            defaultPerPage
        );

        setLocalPerPage(prev => prev === nextPerPage ? prev : nextPerPage);
    }, [perPageLSKey, perPage, defaultPerPage]);

    const updateFormState = () => {
        setFormStateValue(data.reduce((result, current) => {
            if (current[defaultKey]) {
                const { [defaultKey]: id, ...value } = current;
                if (!compareObjects(value, formStateValue[id] || {})) {
                    result[id] = { ...value, [defaultKey]: id };
                } else {
                    result[id] = formStateValue[id];
                }
            }

            return result;
        }, {}));
    };
    // on data updated
    useEffect(() => {
        if (!editable) {
            return;
        }

        const editableRowsCopy = new Map(editableRows);
        const loadableRowsCopy = new Set(loadableRows);
        for (const editedRowData of editableRows) {
            const rowData = data.find(item => {
                return item[defaultKey] === editedRowData[0];
            });

            if (rowData && JSON.stringify(rowData) !== JSON.stringify(editedRowData[1])) {
                editableRowsCopy.delete(rowData[defaultKey]);
                loadableRowsCopy.delete(rowData[defaultKey]);
            }
        }
        setEditableRows(editableRowsCopy);
        setLoadableRows(Array.from(loadableRowsCopy));

        updateFormState();
    }, [data, editable]);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        if (resetEditableRowsToken === null) {
            return;
        }

        setEditableRows(new Map());
        if (setEditKeys) {
            setEditKeys([]);
        }
    }, [resetEditableRowsToken]);

    useEffect(() => {
        const specialColumns = columns.filter((column) => !!column.fixTextareaValue)
            .map(({ dataKey }) => dataKey);

        Array.from(refs.current).forEach(([id, value]) => {
            const stringId = isNumber(id) ? id.toString() : id;
            const currentValues = formStateValue[stringId];

            if (currentValues) {
                Object.keys(currentValues).forEach((key) => {
                    if (specialColumns.includes(key)) {
                        const currentRef = value[key];

                        currentRef.check();
                    }
                })
            }

        })
    }, [refs, formStateValue])

    useEffect(() => {
        Array.from(refs.current).forEach(([id, rowForms]) => {
            if (!Object.prototype.hasOwnProperty.call(extraData, id)) {
                return;
            }

            Object.values(rowForms).forEach(form => {
                form.check();
            });
        });
    }, [extraData]);

    const getDisabled = useCallback((currentFormValue, formControlProps, isMissingDisabledParam) => {
        return formControlProps?.disabledFunc && typeof formControlProps?.disabledFunc === "function" ? formControlProps?.disabledFunc(currentFormValue) : false ||
            formControlProps?.disabled ||
            formControlProps?.disabledOnEdit ||
            isMissingDisabledParam;
    }, []);

    const getRowFieldValue = (rowForms, fieldName, rowData) => {
        const currentForm = rowForms[fieldName];

        if (currentForm && typeof currentForm.getFormValue === "function") {
            const formValue = currentForm.getFormValue() || {};

            if (Object.prototype.hasOwnProperty.call(formValue, fieldName)) {
                return formValue[fieldName];
            }
        }

        const id = rowData[defaultKey];
        const stringId = isNumber(id) ? id.toString() : id;
        const rowFormValue = formStateValue[id] || formStateValue[stringId] || {};

        if (Object.prototype.hasOwnProperty.call(rowFormValue, fieldName)) {
            return rowFormValue[fieldName];
        }

        return rowData[fieldName];
    };

    // on row submit
    const handleSubmitRow = async (rowData) => {
        // check form
        const rowForms = refs.current.get(rowData[defaultKey]);

        if (!rowForms) {
            setEditableRow(rowData, false);
            return;
        }
        let isValid = true;

        for (const form of Object.keys(rowForms)) {
            const currentForm = rowForms[form];

            if (currentForm && typeof currentForm.check === "function" && !currentForm.check()) {
                isValid = false;
            }
        }

        if (!isValid) {
            return;
        }

        // get params
        const params = Object.keys(rowForms)
            .reduce((sum, current) => (
                {
                    ...sum,
                    [current]: getRowFieldValue(rowForms, current, rowData)
                }
            ), { [defaultKey]: rowData[defaultKey] });

        const rowExtraData = extraData[rowData[defaultKey]] || extraData[rowData.id] || {};
        const submitParams = {
            ...params,
            ...rowExtraData
        };

        // check differences
        const isChanged = Object.keys(submitParams)
            .reduce((sum, current) => {
                if (sum === true)
                    return sum;

                const value = (rowData[current] !== null && !isNaN(rowData[current]))
                    ? +submitParams[current]
                    : submitParams[current];
                return rowData[current] !== value;
            }, false);

        if (!isChanged) {
            setEditableRow(rowData, false);
            setLoadableRow(rowData, false);
            return;
        }

        // Approve modal
        if (checkParamsBeforeModify) {
            const notApproved = checkParamsBeforeModify(params);
            if (notApproved) {
                setShowModalApprove(true);
                setModalApproveData(params);
                return;
            }
        }

        if (!onRowSubmit) {
            return;
        }

        // approve modal with text and condition and on submit, remove loading when open. Modal is hear
        // optimize refs array. share callbacks (row edit loading) in function except calss component
        setLoadableRow(rowData, true);

        const res = await onRowSubmit(submitParams, rowData);
        setLoadableRow(rowData, false);

        if (!res) {
            return;
        }

        setEditableRow(rowData, false);

        if (onDataUpdate) {
            onDataUpdate();
        }
    };


    // check before submit
    const handleApprovedSubmit = async () => {
        if (!onRowSubmit) {
            return;
        }
        setModalApproveLoading(true);
        await onRowSubmit(modalApproveData);
        setModalApproveLoading(false);
        setShowModalApprove(false);

        if (onDataUpdate) {
            onDataUpdate();
        }
    };


    // on row delete
    const handleDeleteRow = async () => {
        if (!onRowRemove) {
            return;
        }
        setRemoveDataLoading(true);

        let removeData = modalRemoveData;

        let defaultKeyValue = rowIdKey;
        if (removeData && defaultDisableKey && !!removeData[defaultDisableKey]) {
            defaultKeyValue = defaultRowIdKey;
            setDefaultKey(defaultKeyValue);
        } else {
            setDefaultKey(rowIdKey);
        }

        if (!removeExtended) {
            removeData = modalRemoveData[defaultKeyValue];
        }

        const res = await onRowRemove(removeData);

        if (res) {
            setRemoveDataLoading(false);
            setShowRemoveModal(false);
            setModalRemoveData(null);

            if (onDataUpdate) {
                onDataUpdate();
            }
        }
    };


    const setEditableRow = (rowData, status) => {
        if (rowData.onRowSubmitDefault) {
            rowData.onRowSubmitDefault();
            return;
        }

        let defaultKeyValue = rowIdKey;

        if (rowData && defaultDisableKey && !!rowData[defaultDisableKey]) {
            defaultKeyValue = defaultRowIdKey;
            setDefaultKey(defaultKeyValue);
        } else {
            setDefaultKey(rowIdKey);
        }
        const editableRowsCopy = new Map(editableRows);

        if (editableRows.has(rowData[defaultKeyValue]) && !status) {
            editableRowsCopy.delete(rowData[defaultKeyValue]);
        } else if (!editableRows.has(rowData[defaultKeyValue]) && status) {
            editableRowsCopy.set(rowData[defaultKeyValue], rowData)
        }
        setEditableRows(editableRowsCopy);

        if (setEditKeys)
            setEditKeys(Array.from(editableRowsCopy.keys()));
    };


    const setLoadableRow = (rowData, status) => {
        if (!status) {
            setLoadableRows(loadableRows.filter(item => item !== rowData[defaultKey]));
            return;
        }
        setLoadableRows([...loadableRows, rowData[defaultKey]]);
    };

    const handleChangePage = useCallback((pageValue) => {
        if (getItems) {
            getItems(pageValue, resolvedPerPage);
            return;
        }

        if (onChangePage) {
            onChangePage(pageValue, resolvedPerPage);
        }
    }, [getItems, onChangePage, resolvedPerPage]);

    const handleChangePerPage = useCallback((perPageValue) => {
        if (perPageLSKey) {
            localStorage.setItem(perPageLSKey, perPageValue);
            setLocalPerPage(perPageValue);
        }

        if (getItems) {
            getItems(1, perPageValue);
            return;
        }

        if (onChangePerPage) {
            onChangePerPage(perPageValue);
        }
    }, [getItems, onChangePerPage, perPageLSKey]);

    return (
        <>
            <StyledTable
                data={data}
                loading={loading}
                className={"tableFilters"}
                shouldUpdateScroll={false}
                headerHeight={46}
                rowHeight={46}
                autoHeight
                {...props}
            >


                {/* Row Cells */}

                {columns.map(({
                    dataKey, fieldName, name = "", value, defaultValue, formValue, model, rowModel = null,
                    checkRowModel = false, title = null, flexGrow = null, minWidth = 130, width, fixTextareaValue,
                    customData = false, data = null, modifyData = [], align = "left", showOnlyOnEdit = false, ...formControlProps
                }) => {

                    return ((showOnlyOnEdit && editableRows && Array.from(editableRows.keys()).length || !showOnlyOnEdit) && <Column
                        {...{
                            flexGrow,
                            minWidth,
                            width,
                            align
                        }}>

                        <HeaderCell>{name}</HeaderCell>

                        <Cell {...{ dataKey }}>
                            {(rowData) => {

                                const id = rowData[defaultKey];
                                const stringId = isNumber(id) ? id.toString() : id;
                                const disabledBeforeChangeValue = !editableRows.has(id) && formControlProps?.disabledBeforeChange;

                                // plane value
                                if (!editableRows.has(id) || !formControlProps.accepter) {
                                    const cellValue = (value && value(rowData)) ?? rowData[dataKey];
                                    return (
                                        <span
                                            className="tableFilters__previewText"
                                            title={title ? cellValue : null}
                                        >
                                            {cellValue}
                                        </span>
                                    )
                                }

                                // form value
                                const formControlName = fieldName || dataKey;

                                const formDefaultValue = defaultValue
                                    ? { [formControlName]: defaultValue(rowData) }
                                    : { [formControlName]: rowData[dataKey] };

                                const rowExtraData = extraData[id] || extraData[rowData.id] || {};
                                const currentFormValue = Object.keys(formStateValue).includes(stringId)
                                    ? {
                                        ...formStateValue[stringId],
                                        ...rowExtraData
                                    }
                                    : {
                                        ...formDefaultValue,
                                        ...rowExtraData
                                    };
                                const normalizedFormValue = formValue
                                    ? {
                                        ...currentFormValue,
                                        [formControlName]: formValue(rowData, currentFormValue[formControlName])
                                    }
                                    : currentFormValue;

                                const disabledMissingParamKey = formControlProps?.disabledOnMissingParam ? formControlProps?.disabledOnMissingParam[dataKey] : null;
                                const isMissingDisabledParam = disabledMissingParamKey ? !normalizedFormValue[disabledMissingParamKey] : false;

                                const currentModel = model || formModel;
                                const currentRowModel = rowModel ? rowModel(normalizedFormValue) : null;

                                const currentData = typeof data === "function"
                                    ? data(normalizedFormValue)
                                    : data || [];

                                const isDisabled = getDisabled(normalizedFormValue, formControlProps, isMissingDisabledParam);

                                return (
                                    <Form
                                        model={currentRowModel || currentModel}
                                        formDefaultValue={formDefaultValue}
                                        formValue={normalizedFormValue}
                                        ref={ref => {

                                            if (!ref) {
                                                return;
                                            }

                                            if (refs.current.has(id)) {
                                                const rowRefs = refs.current.get(id);
                                                refs.current.set(id, {
                                                    ...rowRefs,
                                                    [formControlName]: ref
                                                });
                                                return;
                                            }

                                            refs.current.set(id, { [formControlName]: ref });
                                        }}
                                        onChange={(formValue) => {
                                            const value = fixTextareaValue
                                                ? formValue[formControlName].target.value
                                                : formValue[formControlName];


                                            const nextRow = {
                                                ...(formStateValue[id] || {}),
                                                [formControlName]: value
                                            };

                                            flushSync(() => {
                                                setFormStateValue(curr => ({
                                                    ...curr,
                                                    [id]: nextRow
                                                }));
                                            });

                                            if (rowModel && checkRowModel) {
                                                const refsById = refs.current.get(id) || {};
                                                Object.keys(refsById).forEach((name) => {
                                                    const currentRef = refsById[name];
                                                    currentRef.check();
                                                });
                                            }
                                        }}
                                        disabled={disabledBeforeChangeValue}
                                    >
                                        {!formControlProps?.customField ?
                                            <EditField
                                                name={formControlName}
                                                {...(formControlProps.accepter
                                                    && formControlProps.accepter.displayName?.includes("Checkbox") ? {
                                                    defaultChecked: formDefaultValue[dataKey]
                                                } : {
                                                    defaultValue: formDefaultValue[dataKey]
                                                })}

                                                {...formControlProps}
                                                {...(customData ? {
                                                    data: modifyData
                                                } : {
                                                    data: currentData
                                                })}
                                                onOpen={() => {
                                                    if (formControlProps.onOpen) formControlProps.onOpen();
                                                    if (formControlProps.searchable === false) return;
                                                    focusPickerSearch();
                                                }}

                                                {...(formControlProps.onChange ?
                                                    {
                                                        onChange: (value) => {
                                                            formControlProps.onChange(value, rowData);
                                                        }
                                                    } : {}
                                                )}
                                                disabled={isDisabled}
                                            />
                                            : (value && value(rowData, normalizedFormValue)) || rowData[dataKey]}
                                    </Form>
                                );
                            }}
                        </Cell>
                    </Column>)
                })}



                {/* Row Actions */}
                {(editable || extraButtons) &&
                    <Column width={addButtonWidth ? addButtonWidth : 174}>
                        <HeaderCell></HeaderCell>
                        <Cell>
                            {rowData => {
                                const id = rowData[defaultKey];

                                const showCustomButton = rowData?.showCustomButton;
                                const rowLoading = loadableRows.includes(rowData[defaultKey]);
                                const disabledByDefault = defaultDisableKey && defaultDisableKey in rowData && !!rowData[defaultDisableKey];

                                return <div className="tableFilters_buttons">
                                    {extraButtons ? extraButtons(rowData) : null}

                                    {editable && canModify && !showCustomButton &&
                                        <>
                                            {!editableRows.has(rowData[defaultKey])
                                                ? <Button
                                                    size="sm"
                                                    color="blue"
                                                    disabled={rowLoading || extraDisabledModify || (onRowEdit && !modifyPermission)}
                                                    loading={extraDisabledModify}
                                                    onClick={() => {
                                                        if (onRowEdit) {
                                                            onRowEdit(rowData);
                                                            return;
                                                        }

                                                        setEditableRow(rowData, true);
                                                    }}
                                                >
                                                    <Icon icon="edit2" />
                                                </Button>
                                                : <>
                                                    <Button
                                                        size="sm"
                                                        color="green"
                                                        disabled={rowLoading || !modifyPermission}
                                                        onClick={() => handleSubmitRow(rowData)}
                                                    >
                                                        <Icon icon="check-circle" />
                                                    </Button>

                                                    <Button
                                                        size="sm"
                                                        color="red"
                                                        disabled={rowLoading}
                                                        onClick={() => {
                                                            if (changeExtraData) {
                                                                changeExtraData((prev) => {
                                                                    const prevClone = { ...prev };

                                                                    prevClone[rowData.id] = { ...rowData };
                                                                    return prevClone;
                                                                });
                                                            }

                                                            if (clearOnCancel) {
                                                                setFormStateValue((currentState) => {
                                                                    const currentStateCopy = { ...currentState };
                                                                    return {
                                                                        ...currentStateCopy,
                                                                        [id]: rowData
                                                                    };
                                                                });
                                                            }
                                                            setEditableRow(rowData, false);
                                                        }}
                                                    >
                                                        <Icon icon="close-circle" />
                                                    </Button>
                                                </>
                                            }
                                        </>}
                                    {editable && showCustomButton && <>
                                        {customButton(rowData?.customButtonText)}
                                    </>}

                                    {editable && canRemove && !showCustomButton &&
                                        <Button
                                            size="sm"
                                            color="red"
                                            disabled={rowLoading || disabledByDefault || !removePermission || rowData.default || rowData.undeletable}
                                            onClick={() => {
                                                setModalRemoveData(rowData);
                                                setShowRemoveModal(true);
                                            }}
                                        >
                                            <Icon icon="trash2" />
                                        </Button>
                                    }
                                </div>
                            }}
                        </Cell>
                    </Column>
                }

            </StyledTable >

            {showFormCreate && canCreate &&
                <>
                    <Spacer />
                    <FormCreateRowNew
                        mobile={mobile}
                        columns={columns}
                        formModel={formModel}
                        formStyle={formStyle}
                        disabled={!createPermission}
                        addButtonWidth={addButtonWidth}
                        extraDisabledCreate={extraDisabledCreate}
                        formDefaultValue={createFormDefaultValue}

                        onDataUpdate={onDataUpdate}
                        onSubmit={(params, defaultData) => {
                            const data = {
                                ...params,
                                ...createExtraData
                            };

                            return onRowCreate(data, defaultData);
                        }}
                        extraOnChange={extraOnChange}
                        extraOnSubmit={extraOnSubmit}
                        onChangeServiceName={onChangeServiceName}
                    />
                </>
            }

            {pagination &&
                <>
                    <Spacer />
                    <Pagination
                        total={count}
                        {...{
                            perPage: resolvedPerPage,
                            activePage,
                            onChangePage: handleChangePage,
                            onChangePerPage: handleChangePerPage
                        }}
                        noWrapper
                    />
                </>}

            <ModalRemoveRow
                show={showRemoveModal}
                title={modalRemoveTitle}
                onSubmit={handleDeleteRow}
                onClose={() => setShowRemoveModal(false)}
                disabled={removeDataLoading}
            />

            <ModalSubmitRowApprove
                show={showModalApprove}
                onSubmit={handleApprovedSubmit}
                onClose={() => setShowModalApprove(false)}
                disabled={modalApproveLoading}
            />

        </>
    )
}

const StyledTable = styled(BaseTable)`
    && {
        .rs-table-cell-content,
        .rs-form,
        .rs-form-control-wrapper,
        .tableFilters_field {
            min-width: 0;
        }

        .rs-form-control-wrapper,
        .rs-form-control-wrapper > *,
        .rs-form,
        .tableFilters_field,
        .tableFilters_field.rs-picker,
        .tableFilters_field.rs-picker-input,
        .tableFilters_field.rs-input,
        .tableFilters_field.rs-input-number {
            max-width: 100%;
            width: 100%;
        }

        .tableFilters_field .rs-picker-toggle,
        .tableFilters_field .rs-picker-toggle-value,
        .tableFilters_field .rs-picker-toggle-placeholder,
        .tableFilters_field .rs-picker-toggle-clean {
            min-width: 0;
        }

        .tableFilters_field .rs-picker-toggle-value,
        .tableFilters_field .rs-picker-toggle-placeholder {
            display: block;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .rs-table-row:not(.rs-table-row-header) .rs-table-cell-content {
            display: flex;
            align-items: center;
        }

        .rs-table-row:not(.rs-table-row-header) .rs-table-cell-content > * {
            min-width: 0;
        }

        .rs-form-control-wrapper .rs-form-error-message-wrapper {
            top: 0;
            right: 4px;
            bottom: auto;
            left: 4px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            height: 100%;
            pointer-events: none;
        }

        .rs-form-control-wrapper .rs-form-error-message.rs-form-error-message-show {
            position: absolute;
            box-sizing: border-box;
            max-width: 100%;
            height: 18px;
            padding: 0 6px;
            top: -2px;
            right: 12px;
            overflow: hidden;
            border: 0;
            border-radius: 10px;
            line-height: 18px;
            text-overflow: ellipsis;
        }

        .rs-form-control-wrapper .rs-form-error-message-arrow {
            display: none;
        }

        .rs-form-control-wrapper .rs-form-error-message-inner {
            display: block;
            overflow: hidden;
            font-size: 11px;
            text-overflow: ellipsis;
        }
    
        .tableFilters__previewText {
            display: block;
            line-height: 20px;
            word-break: normal;
        }
        
        .tableFilters_buttons {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 5px;
            min-height: 28px;
        }

        .tableFilters_buttons .rs-btn {
            position: relative;
            width: 28px;
            height: 28px;
            padding: 2px;
        }

        .tableFilters_buttons .rs-btn > .rs-icon {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 16px;
            height: 16px;
            margin: 0;
            transform: translate(-50%, -50%);
        }

        .tableFilters_buttons .rs-btn > .rs-icon[data-icon] {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 16px;
        }
    }

    /* .rs-table-cell-header .rs-table-cell-content {
        display: flex;
        align-items: center;
    } */
`;

const EditField = styled(Form.Control).attrs(() => {
    return {
        className: "tableFilters_field",
        errorPlacement: "topEnd",
    }
})`
    && {
        max-width: 100%;
        min-width: 0;
        width: 100%;
    }

    && .rs-picker-toggle,
    &&.rs-input,
    &&.rs-input-number {
        max-width: 100%;
        min-width: 0;
        width: 100%;
    }

    && {
        ${({ style }) => {
        if (!style) return "";

        const allowedProps = [
            "fontSize",
            "height",
            "color",
            "textDecoration",
            "fontWeight",
            "fontStyle",
            "lineHeight"
        ];

        const cssLines = Object.entries(style)
            .filter(([key]) => allowedProps.includes(key))
            .map(([key, value]) => {
                const cssKey = key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
                const cssValue = typeof value === "number" ? `${value}px` : value;
                return `${cssKey}: ${cssValue};`;
            })
            .join("\n");

        return css`
                a {
                    ${cssLines}
                }
            `;
    }}
    }
`;

